import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center font-medium rounded-2xl",
    "transition-all duration-300 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:opacity-40 disabled:cursor-not-allowed disabled:pointer-events-none",
    "motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-sands-600 text-white hover:bg-sands-700 active:bg-sands-800",
          "hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]",
        ].join(" "),
        secondary: [
          "bg-sands-100 hover:bg-sands-200 border-sands-300 border-2 text-sands-700",
          "hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]",
        ].join(" "),
        ghost:
          "text-sands-600 hover:bg-sands-100 hover:text-sands-800",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
      },
      size: {
        default: "px-6 py-3 text-base",
        sm: "px-4 py-2 text-sm rounded-xl",
        lg: "px-8 py-4 text-lg",
        icon: "h-10 w-10 rounded-xl p-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = "button", ...props }, ref) => (
    <button
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      ref={ref}
      {...props}
    />
  ),
);
Button.displayName = "Button";

export { buttonVariants };
